import { AttachmentService } from '../../services/DataService';
import { ContactAttachment } from '../../types/contacts';
import { callAdminApi, wixImageToStaticUrl } from './wixAdminApi';
import { ApiErrorCode, createApiError } from '../../types/errors';

export class AttachmentsApi implements AttachmentService {
  async getAttachments(contactId: string): Promise<ContactAttachment[]> {
    try {
      const response = await callAdminApi('listAttachments', 'attachments', {
        contactId
      });

      if (!response.attachments || !Array.isArray(response.attachments)) {
        return [];
      }

      return response.attachments.map((att: {
        _id?: string;
        id?: string;
        downloadUrl?: string;
        url?: string;
        fileName?: string;
        name?: string;
        mimeType?: string;
        type?: string;
        size?: number;
        uploadDate?: string;
        date?: string;
      }): ContactAttachment => ({
        id: String(att._id || att.id || ''),
        downloadUrl: String(att.downloadUrl || att.url || ''),
        name: String(att.fileName || att.name || 'Unknown'),
        type: String(att.mimeType || att.type || 'application/octet-stream'),
        size: Number(att.size || 0),
        date: String(att.uploadDate || att.date || new Date().toISOString()),
        url: String(att.downloadUrl || att.url || ''),
        uploadDate: String(att.uploadDate || att.date || new Date().toISOString()),
        contactId: contactId
      }));
    } catch (error: unknown) {
      const apiError = createApiError(
        ApiErrorCode.DATA_NOT_FOUND,
        error instanceof Error ? error.message : 'Failed to get attachments'
      );
      throw apiError;
    }
  }

  async addAttachments(contactId: string, file: File): Promise<ContactAttachment> {

    try {
      // Reference validateFileType to satisfy TS6133
      void this.validateFileType(file.name, file.type);
      console.log('Starting file upload for:', file.name);

      // Convert file to base64
      const fileData = await this.fileToBase64(file);

      // Call the function to do the upload
      const uploadResponse = await callAdminApi('uploadAttachment', 'attachments', {
        contactId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileData
      });

      if (!uploadResponse.success) {
        throw new Error('Upload failed');
      }

      // Business logic: Wait and get attachment details
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const attachmentsList = await callAdminApi('listAttachments', 'attachments', { contactId });
      
      const attachments = Array.isArray(attachmentsList.attachments) ? attachmentsList.attachments : [];
      const newAttachment = attachments.find(
        (att: { fileName?: string }) => att.fileName === file.name
      ) || attachments[0];

      return {
        id: String(newAttachment?._id || uploadResponse.uploadId || `upload_${Date.now()}`),
        downloadUrl: String(newAttachment?.downloadUrl || ''),
        name: String(newAttachment?.fileName || file.name),
        type: String(newAttachment?.mimeType || file.type),
        size: Number(newAttachment?.size || file.size),
        date: String(newAttachment?.uploadDate || new Date().toISOString()),
        url: String(newAttachment?.downloadUrl || ''),
        uploadDate: String(newAttachment?.uploadDate || new Date().toISOString()),
        contactId: contactId
      };

    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }

  }  async deleteAttachment(contactId: string, attachmentId: string): Promise<boolean> {
    try {
      const response = await callAdminApi('deleteAttachment', 'attachments', {
        contactId,
        attachmentId
      });

      return response.success === true;
    } catch (error: unknown) {
      const apiError = createApiError(
        ApiErrorCode.SERVER_ERROR,
        error instanceof Error ? error.message : 'Failed to delete attachment'
      );
      throw apiError;
    }
  }

  async openAttachment(attachment: ContactAttachment): Promise<void> {
    try {
      // Check if we have a valid HTTP/HTTPS URL
      const isValidHttpUrl = (url: string): boolean => {
        return url.startsWith('http://') || url.startsWith('https://');
      };

      // Reference convertWixImageToStaticUrl to satisfy TS6133
      void this.convertWixImageToStaticUrl(attachment.url || '');

      // If we have a valid HTTP downloadUrl, use it
      if (attachment.downloadUrl && attachment.downloadUrl.trim() && isValidHttpUrl(attachment.downloadUrl)) {
        window.open(attachment.downloadUrl, '_blank', '');
        return;
      }

      // If we have a valid HTTP url, use it
      if (attachment.url && attachment.url.trim() && isValidHttpUrl(attachment.url)) {
        window.open(attachment.url, '_blank', '');
        return;
      }

      // Get attachment download URL (no proxy, just get the URL and open it)
      const downloadResponse = await this.generateDownloadUrlWithFallback(
        attachment.contactId,
        attachment.id
      );

      if (downloadResponse.downloadUrl && typeof downloadResponse.downloadUrl === 'string') {
        if (isValidHttpUrl(downloadResponse.downloadUrl)) {
          // Open direct HTTP(S) download URL
          window.open(downloadResponse.downloadUrl, '_blank', '');
        } else {
          // Unknown format, try to open anyway
          window.open(downloadResponse.downloadUrl, '_blank', '');
        }
      } else {
        throw new Error('Could not get valid download URL from API');
      }

    } catch (error) {
      const apiError = createApiError(
        ApiErrorCode.SERVER_ERROR,
        error instanceof Error ? error.message : 'Failed to open attachment'
      );
      alert(`Unable to open attachment "${attachment.name}". This appears to be a limitation with Wix's attachment system.`);
      throw apiError;
    }
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = error => reject(error);
    });
  }

  async generateAttachmentUploadUrl(contactId: string, fileName: string, options?: { mimeType?: string }): Promise<{ uploadUrl: string; uploadId?: string }> {
    const response = await callAdminApi('generateAttachmentUploadUrl', 'attachments', {
      contactId,
      fileName,
      options
    });

    // Type assertion to ensure the response has the required properties
    return {
      uploadUrl: String(response.uploadUrl || ''),
      uploadId: response.uploadId ? String(response.uploadId) : undefined
    };
  }

  async getAttachmentDetails(contactId: string, attachmentId: string): Promise<Record<string, unknown>> {
    try {
      const response = await callAdminApi('getAttachment', 'attachments', {
        contactId,
        attachmentId
      });

      return {
        success: true,
        attachmentId: response.attachmentId,
        fileName: response.fileName,
        mimeType: response.mimeType,
        attachmentType: response.attachmentType,
        downloadUrl: response.downloadUrl,
        uploadDate: response.uploadDate,
        size: response.size,
        contactId: contactId,
        image: response.image
      };
    } catch (error) {
      throw error;
    }
  }

  async generateDownloadUrl(contactId: string, attachmentId: string): Promise<{ downloadUrl: string; success: boolean }> {
    const response = await this.generateDownloadUrlWithFallback(contactId, attachmentId);

    return {
      downloadUrl: response.downloadUrl,
      success: response.success
    };
  }

  async convertWixImageUrl(wixImageUrl: string): Promise<{ downloadUrl: string; originalUrl?: string; fallback?: boolean }> {
    return this.convertWixImageUrlWithFallback(wixImageUrl);
  }

  async downloadAttachment(contactId: string, attachmentId: string): Promise<string> {
    try {
      // Use the Firebase function to generate download URL
      const downloadResponse = await this.generateDownloadUrlWithFallback(contactId, attachmentId);

      if (downloadResponse.downloadUrl && typeof downloadResponse.downloadUrl === 'string') {
        return downloadResponse.downloadUrl;
      } else {
        const apiError = createApiError(
          ApiErrorCode.DATA_NOT_FOUND,
          'Could not generate download URL'
        );
        throw apiError;
      }
    } catch (error) {
      const apiError = createApiError(
        ApiErrorCode.SERVER_ERROR,
        error instanceof Error ? error.message : 'Failed to download attachment'
      );
      throw apiError;
    }
  }
  private convertWixImageToStaticUrl(wixUrl: string): string {
    if (!wixUrl || !wixUrl.startsWith('wix:image://')) {
      return wixUrl;
    }

    const urlParts = wixUrl.replace('wix:image://', '').split('/');
    if (urlParts.length >= 1) {
      const imageId = urlParts[0].split('#')[0];
      return `https://static.wixstatic.com/media/${imageId}/v1/fill/w_1000,h_1000,al_c,q_80/file.png`;
    }

    return `https://static.wixstatic.com/media/${wixUrl}`;
  }

  private validateFileType(fileName: string, mimeType: string): { isValid: boolean; error?: string } {
    const supportedMimeTypes = [
      /^image\//,
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const supportedExtensions = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx'];

    const ext = (fileName.match(/\.[^.]+$/) || [''])[0].toLowerCase();

    if (ext === '.txt' || ext === '.text') {
      return {
        isValid: false,
        error: 'Unsupported file extension. Allowed: .jpg, .jpeg, .png, .pdf, .doc, .docx'
      };
    }

    if (!supportedExtensions.includes(ext)) {
      return {
        isValid: false,
        error: 'Unsupported file extension. Allowed: .jpg, .jpeg, .png, .pdf, .doc, .docx'
      };
    }

    const isSupported = supportedMimeTypes.some(mt =>
      typeof mt === 'string' ? mimeType === mt : mt.test(mimeType)
    );

    if (!isSupported) {
      return {
        isValid: false,
        error: `Wix does not support attachments of type "${mimeType}". Allowed: images, PDF, Word.`
      };
    }

    return { isValid: true };
  }

  async uploadAttachmentWithValidation(contactId: string, fileName: string, fileType: string, fileSize: number, fileData: string): Promise<Record<string, unknown>> {
    try {
      // Use the Firebase function which already has all validation logic
      const response = await callAdminApi('uploadAttachment', 'attachments', {
        contactId,
        fileName,
        fileType,
        fileSize,
        fileData
      });

      // Handle API errors with code/message
      if (response && response.code && response.message) {
        const apiError = createApiError(
          ApiErrorCode.DATA_INVALID_FORMAT,
          response.message.toString()
        );
        throw apiError;
      }

      if (!response.success) {
        const apiError = createApiError(
          ApiErrorCode.SERVER_ERROR,
          'Upload failed'
        );
        throw apiError;
      }

      return {
        success: true,
        attachmentId: response.attachmentId,
        fileName: response.fileName || fileName,
        mimeType: response.mimeType || fileType,
        attachmentType: response.attachmentType || (fileType.startsWith('image/') ? 'IMAGE' : 'DOCUMENT'),
        downloadUrl: response.downloadUrl || '',
        uploadDate: response.uploadDate || new Date().toISOString(),
        size: response.size || fileSize,
        contactId: contactId
      };

    } catch (error) {
      const apiError = createApiError(
        ApiErrorCode.SERVER_ERROR,
        error instanceof Error ? error.message : 'Upload failed'
      );
      throw apiError;
    }
  } async listAttachmentsWithPreview(contactId: string, paging?: Record<string, unknown>): Promise<{ attachments: Record<string, unknown>[] }> {
    const rawList = await callAdminApi('listAttachments', 'attachments', { contactId, paging }); const attachmentsArray = Array.isArray(rawList.attachments) ? rawList.attachments : []; const attachments = attachmentsArray.map((att: Record<string, unknown>) => {
      let previewUrl = '';
      if (att.attachmentType === 'IMAGE' && typeof att.image === 'string') {
        previewUrl = wixImageToStaticUrl(att.image);
      } else if (att.attachmentType === 'OTHER' && typeof att.document === 'string') {
        previewUrl = att.document;
      }
      return {
        ...att,
        previewUrl
      };
    });
    return { attachments };
  }

  async getAttachmentWithFallback(contactId: string, attachmentId: string): Promise<Record<string, unknown>> {
    try {
      const identifiers = {
        attachmentId: attachmentId,
        contactId: contactId
      };

      const response = await callAdminApi('getAttachment', 'attachments', identifiers);
      const attachment = response;

      return {
        success: true,
        attachmentId: attachment._id,
        fileName: attachment.fileName,
        mimeType: attachment.mimeType,
        attachmentType: attachment.attachmentType,
        downloadUrl: attachment.previewImage || '',
        uploadDate: attachment.uploadDate || new Date().toISOString(),
        size: attachment.size || 0,
        contactId: contactId
      };

    } catch (getError) {
      // Fallback to listAttachments
      try {
        const listResponse = await callAdminApi('listAttachments', 'attachments', { contactId });

        if (!listResponse.attachments || !Array.isArray(listResponse.attachments) || listResponse.attachments.length === 0) {
          throw new Error('No attachments found for this contact');
        }

        const attachment = listResponse.attachments.find((att: Record<string, unknown>) => att._id === attachmentId);

        if (!attachment) {
          throw new Error(`Attachment with ID ${attachmentId} not found`);
        }

        return {
          success: true,
          attachmentId: attachment._id,
          fileName: attachment.fileName,
          mimeType: attachment.mimeType,
          attachmentType: attachment.attachmentType,
          downloadUrl: attachment.previewImage || '',
          uploadDate: attachment.uploadDate || new Date().toISOString(),
          size: attachment.size || 0,
          contactId: contactId
        };

      } catch (listError: unknown) {
        const apiError = createApiError(
          ApiErrorCode.DATA_NOT_FOUND,
          `Cannot retrieve attachment: ${getError instanceof Error ? getError.message : String(getError)}. List error: ${listError instanceof Error ? listError.message : String(listError)}`
        );
        throw apiError;
      }
    }
  }
  async uploadAttachmentWithFullValidation(contactId: string, fileName: string, fileType: string, fileData: string): Promise<{
    success: boolean;
    attachmentId: string;
    fileName: string;
    mimeType: string;
    attachmentType: string;
    downloadUrl: string;
    uploadDate: string;
    size: number;
    contactId: string;
  }> {
    try {
      // Use the private validateFileType method to check file type
      const validation = this.validateFileType(fileName, fileType);
      if (!validation.isValid) {
        throw new Error('UNSUPPORTED_FILE_EXTENSION: ' + (validation.error || 'Invalid file type'));
      }

      // Use the Firebase function which already has all validation logic
      const response = await callAdminApi('uploadAttachment', 'attachments', {
        contactId,
        fileName,
        fileType,
        fileData
      });

      // Handle API errors with code/message
      if (response && response.code && response.message) {
        const apiError = createApiError(
          ApiErrorCode.DATA_INVALID_FORMAT,
          String(response.message)
        );
        throw apiError;
      }

      if (!response.success) {
        const apiError = createApiError(
          ApiErrorCode.SERVER_ERROR,
          'Upload failed'
        );
        throw apiError;
      }

      return {
        success: true,
        attachmentId: String(response.attachmentId || ''),
        fileName: String(response.fileName || fileName),
        mimeType: String(response.mimeType || fileType),
        attachmentType: String(response.attachmentType || (fileType.startsWith('image/') ? 'IMAGE' : 'DOCUMENT')),
        downloadUrl: String(response.downloadUrl || ''),
        uploadDate: String(response.uploadDate || new Date().toISOString()),
        size: Number(response.size || Buffer.from(fileData, 'base64').length),
        contactId: contactId
      };

    } catch (error) {
      const apiError = createApiError(
        ApiErrorCode.SERVER_ERROR,
        error instanceof Error ? error.message : 'Upload failed'
      );
      throw apiError;
    }

  } async generateDownloadUrlWithFallback(contactId: string, attachmentId: string): Promise<{
    success: boolean;
    downloadUrl: string;
    attachmentId: string;
    contactId: string;
    fileName: string;
    mimeType: string;
    isDataUrl: boolean;
    isPublic?: boolean;
    warning?: string;
  }> {
    try {
      // Try to get attachment details first
      const attachment = await callAdminApi('getAttachment', 'attachments', { contactId, attachmentId });

      // Try to use the attachment's document field to get fileId
      let fileId: string | null = null;
      if (attachment.document && typeof attachment.document === 'string' && attachment.document.startsWith('wix:document://v1/')) {
        const match = attachment.document.match(/^wix:document:\/\/v1\/([a-zA-Z0-9]+)/);
        fileId = match ? match[1] : null;
      }

      // Try generateFileDownloadUrl for private files
      if (fileId) {
        try {
          const fileIdVariants = [
            fileId,
            `media/${fileId}`,
            `media/${fileId}.pdf`,
            `media/${fileId}.docx`,
            `media/${fileId}.doc`,
            `media/${fileId}.txt`,
            `media/${fileId}.png`,
            `media/${fileId}.jpg`,
            `media/${fileId}.jpeg`
          ];

          for (const variant of fileIdVariants) {
            try {
              const downloadResult = await callAdminApi('generateFileDownloadUrl', 'files', { fileId: variant });
              if (downloadResult && Array.isArray(downloadResult.downloadUrls) && downloadResult.downloadUrls[0]?.url) {
                const url = String(downloadResult.downloadUrls[0].url);
                const isPublic = url.includes('static.wixstatic.com') && !url.includes('download-files.wixmp.com');

                return {
                  success: true,
                  downloadUrl: url,
                  attachmentId,
                  contactId,
                  fileName: String(attachment.fileName || ''),
                  mimeType: String(attachment.mimeType || ''),
                  isDataUrl: false,
                  isPublic
                };
              }
            } catch (err) {
              createApiError(
                ApiErrorCode.DATA_NOT_FOUND,
                err instanceof Error ? err.message : 'Variant lookup failed'
              );
            }
          }
        } catch (err) {
          const apiError = createApiError(
            ApiErrorCode.SERVER_ERROR,
            err instanceof Error ? err.message : 'generateFileDownloadUrl failed'
          );
          throw apiError;
        }
      }

      // Try generateAttachmentDownloadUrl
      try {
        const downloadResponse = await callAdminApi('generateAttachmentDownloadUrl', 'attachments', { contactId, attachmentId });
        if (downloadResponse && downloadResponse.downloadUrl) {
          return {
            success: true,
            downloadUrl: String(downloadResponse.downloadUrl),
            attachmentId,
            contactId,
            fileName: String(attachment.fileName || ''),
            mimeType: String(attachment.mimeType || ''),
            isDataUrl: false
          };
        }
      } catch (err) {
        const apiError = createApiError(
          ApiErrorCode.SERVER_ERROR,
          err instanceof Error ? err.message : 'generateAttachmentDownloadUrl failed'
        );
        throw apiError;
      }

      // Fallback to static URL
      const attachmentsList = await callAdminApi('listAttachments', 'attachments', { contactId });
      const attachmentsArray = Array.isArray(attachmentsList.attachments) ? attachmentsList.attachments : [];

      const found = attachmentsArray.find((att: Record<string, unknown>) =>
        att._id === attachmentId || att.id === attachmentId
      );

      if (found && typeof found.document === 'string' && found.document.startsWith('wix:document://v1/')) {
        const match = found.document.match(/^wix:document:\/\/v1\/([a-zA-Z0-9]+)/);
        const staticFileId = match ? match[1] : null;
        if (staticFileId) {
          const staticUrl = `https://static.wixstatic.com/media/${staticFileId}`;
          return {
            success: true,
            downloadUrl: staticUrl,
            attachmentId,
            contactId,
            fileName: String(found.fileName || ''),
            mimeType: String(found.mimeType || ''),
            isDataUrl: false,
            warning: 'This is a static Wix URL. If the file is private, it may not be accessible.'
          };
        }
      }

      throw new Error('Could not generate download URL');

    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }

  } async convertWixImageUrlWithFallback(wixImageUrl: string): Promise<{ downloadUrl: string; originalUrl?: string; fallback?: boolean }> {
    try {
      if (wixImageUrl.startsWith('wix:image://')) {
        // Try to use media utility
        const imageUrl = await callAdminApi('getImageUrl', 'media', { wixImageUrl });
        return {
          downloadUrl: String(imageUrl.url || ''),
          originalUrl: wixImageUrl
        };
      } else {
        return { downloadUrl: wixImageUrl };
      }
    } catch (error) {
      // Fallback: extract file ID and create static URL
      if (wixImageUrl.startsWith('wix:image://v1/')) {
        const fileId = wixImageUrl.split('/')[3].split('#')[0];
        const fallbackUrl = `https://static.wixstatic.com/media/${fileId}`;

        return {
          downloadUrl: fallbackUrl,
          originalUrl: wixImageUrl,
          fallback: true
        };
      } else {
        throw new Error(`Failed to convert Wix image URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }

  async debugAttachments(): Promise<Record<string, unknown>> {
    try {
      const response = await callAdminApi('debugAttachments', 'attachments', {});
      return response as Record<string, unknown>;
    } catch (error) {
      console.error('Debug failed:', error);
      throw error;
    }
  }

  async debugWixClient(): Promise<Record<string, unknown>> {
    try {
      const response = await callAdminApi('debugWixClient', 'attachments', {});
      return response as Record<string, unknown>;
    } catch (error) {
      console.error('Debug failed:', error);
      throw error;
    }
  }

  // Helper method to handle file operations that might return various response types
  private handleApiResponse<T>(response: unknown, defaultValue: T): T {
    if (response && typeof response === 'object') {
      return response as T;
    }
    return defaultValue;
  }

  // Method to safely get attachment list with proper typing
  async getAttachmentsList(contactId: string): Promise<ContactAttachment[]> {
    try {
      const response = await callAdminApi('listAttachments', 'attachments', {
        contactId
      });

      // Handle the response safely
      const attachmentsData = this.handleApiResponse(response, { attachments: [] });

      if (!attachmentsData.attachments || !Array.isArray(attachmentsData.attachments)) {
        return [];
      }

      return attachmentsData.attachments.map((att: Record<string, unknown>): ContactAttachment => ({
        id: String(att._id || att.id || ''),
        downloadUrl: String(att.downloadUrl || att.url || ''),
        name: String(att.fileName || att.name || 'Unknown'),
        type: String(att.mimeType || att.type || 'application/octet-stream'),
        size: Number(att.size || 0),
        date: String(att.uploadDate || att.date || new Date().toISOString()),
        url: String(att.downloadUrl || att.url || ''),
        uploadDate: String(att.uploadDate || att.date || new Date().toISOString()),
        contactId: contactId
      }));
    } catch (error) {
      console.error('Error getting attachments list:', error);
      return [];
    }
  }

  // Method to safely handle attachment operations with proper error handling
  async safeAttachmentOperation<T>(
    operation: () => Promise<T>,
    defaultValue: T,
    errorMessage: string
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      console.error(errorMessage, error);
      return defaultValue;
    }
  }
}
